import axios from 'axios';
import { io } from 'socket.io-client';

// One connection to a community server (BLOBL Node): node token, REST calls and the WebSocket.
// Node tokens come from the Core, are valid for one node only and live 15 minutes. They are kept
// in memory only, never in localStorage.

const TOKEN_EARLY_MS = 3 * 60 * 1000;      // fetch a new token when less than this is left
const SOCKET_REFRESH_MS = 12 * 60 * 1000;  // send a fresh token to an open socket
const REQUEST_TIMEOUT_MS = 15000;

export class NodeError extends Error {
  // kind: 'unavailable' (Core gives no token: blocked, removed), 'not_member', 'blocked', 'offline', 'error'
  constructor(kind, message, status) {
    super(message || kind);
    this.kind = kind;
    this.status = status;
  }
}

const coreHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function toNodeError(err) {
  if (err instanceof NodeError) return err;
  const status = err.response?.status;
  const data = err.response?.data || {};
  if (!err.response) return new NodeError('offline', "Can't reach this server right now");
  if (data.not_member) return new NodeError('not_member', data.error, status);
  if (data.blocked) return new NodeError('blocked', data.error, status);
  return new NodeError('error', typeof data.error === 'string' ? data.error.slice(0, 200) : 'Something went wrong', status);
}

export class NodeConnection {
  constructor(node, { onEvent, onSocketState } = {}) {
    this.node = node; // { id, url } from the Core, never from an invite link
    this.onEvent = onEvent || (() => {});
    this.onSocketState = onSocketState || (() => {});
    this.token = null;
    this.tokenExp = 0;
    this.tokenPromise = null;
    this.socket = null;
    this.status = 'online';
    this.refreshTimer = null;
  }

  async getToken(force = false) {
    if (!force && this.token && Date.now() < this.tokenExp - TOKEN_EARLY_MS) return this.token;
    if (!this.tokenPromise) {
      this.tokenPromise = axios.post(`/api/nodes/${this.node.id}/token`, {}, { headers: coreHeaders() })
        .then(res => {
          this.token = res.data.token;
          this.tokenExp = Date.now() + (Number(res.data.expires_in) || 900) * 1000;
          return this.token;
        })
        .catch(err => {
          throw err.response?.status === 404 ? new NodeError('unavailable', 'This server is not available') : toNodeError(err);
        })
        .finally(() => { this.tokenPromise = null; });
    }
    return this.tokenPromise;
  }

  // REST call to the node; a rejected token is renewed once
  async request(method, path, data, { retried = false, ...opts } = {}) {
    const token = await this.getToken();
    try {
      const res = await axios({
        method,
        url: this.node.url + path,
        data,
        timeout: REQUEST_TIMEOUT_MS,
        headers: { Authorization: `Bearer ${token}` },
        ...opts,
      });
      return res.data;
    } catch (err) {
      if (err.response?.status === 401 && !retried) {
        await this.getToken(true);
        return this.request(method, path, data, { retried: true, ...opts });
      }
      throw toNodeError(err);
    }
  }

  get = (path, opts) => this.request('get', path, undefined, opts);
  post = (path, data, opts) => this.request('post', path, data, opts);
  patch = (path, data) => this.request('patch', path, data);
  put = (path, data) => this.request('put', path, data);
  del = (path) => this.request('delete', path);

  get connected() { return !!this.socket?.connected; }

  connect() {
    if (this.socket) return;
    let everConnected = false;
    const socket = io(this.node.url, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: (cb) => {
        this.getToken().then(token => cb({ token, status: this.status })).catch(() => cb({}));
      },
    });
    this.socket = socket;

    socket.on('connect', () => {
      this.onSocketState('connected');
      if (everConnected) this.onEvent('resync');
      everConnected = true;
    });
    socket.on('disconnect', (reason) => {
      this.onSocketState('disconnected');
      // The node closed it (token expired, server restarted): connect again with a fresh token
      if (reason === 'io server disconnect' && this.socket === socket) {
        setTimeout(() => { if (this.socket === socket) { this.token = null; socket.connect(); } }, 1000);
      }
    });
    let failures = 0;
    socket.on('connect', () => { failures = 0; });
    socket.on('connect_error', (err) => {
      this.onSocketState(err.message === 'blocked' ? 'blocked' : 'error');
      if (err.message === 'unauthorized') this.token = null;
      // Rejected by the node's middleware: socket.io doesn't retry those by itself.
      // Try again with a fresh token, slower each time; a blocked node is not retried.
      if (!socket.active && err.message !== 'blocked' && this.socket === socket) {
        failures += 1;
        const delay = Math.min(60000, 2000 * 2 ** Math.min(failures, 5));
        setTimeout(() => { if (this.socket === socket && !socket.connected) socket.connect(); }, delay);
      }
    });
    socket.onAny((event, data) => this.onEvent(event, data));

    clearInterval(this.refreshTimer);
    this.refreshTimer = setInterval(async () => {
      if (!socket.connected) return;
      try {
        const token = await this.getToken(true);
        socket.emit('auth:refresh', { token });
      } catch {}
    }, SOCKET_REFRESH_MS);
  }

  disconnect() {
    clearInterval(this.refreshTimer);
    this.refreshTimer = null;
    const s = this.socket;
    this.socket = null;
    s?.disconnect();
    this.onSocketState('disconnected');
  }

  setStatus(status) {
    this.status = status;
    this.socket?.emit('presence:set', { status });
  }

  emit(event, data) {
    this.socket?.emit(event, data);
  }
}
